import React, { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { auth, db } from "../lib/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { signOut, sendEmailVerification } from "firebase/auth";
import { 
  Dialog, DialogTitle, DialogContent, 
  DialogContentText, DialogActions, Button,
  CircularProgress, 
} from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoadingSkeleton from "../LoadingSkeleton/LoadingSkeleton";

const Protected = ({ allowedRoles }) => {
  const [authState, setAuthState] = useState({
    loading: true,
    user: null,
    authUser: null,
    showVerificationDialog: false
  });
  const [emailResent, setEmailResent] = useState(false);
  const [resending, setResending] = useState(false);

  // Security measures
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleSelectStart = (e) => e.preventDefault();
    
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("selectstart", handleSelectStart);
    
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("selectstart", handleSelectStart);
    };
  }, []);

  // Auth state management
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (!authUser) {
        return setAuthState(prev => ({
          ...prev,
          loading: false,
          authUser: null,
          user: null
        }));
      }

      // Check email verification first
      if (!authUser.emailVerified) {
        return setAuthState(prev => ({
          ...prev,
          loading: false,
          authUser,
          showVerificationDialog: true
        }));
      }

      // Get additional user data from Firestore if email is verified
      const userRef = doc(db, "users", authUser.uid);
      const userSnapshot = await getDoc(userRef);
      
      if (userSnapshot.exists()) {
        const userData = userSnapshot.data();
        localStorage.setItem("user", JSON.stringify(userData));
        setAuthState(prev => ({
          ...prev,
          loading: false,
          user: userData,
          authUser
        }));
      } else {
        // User data not found in database
        setAuthState(prev => ({
          ...prev,
          loading: false,
          authUser
        }));
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("user");
      setAuthState(prev => ({
        ...prev,
        user: null,
        authUser: null,
        showVerificationDialog: false
      }));
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleResendVerification = async () => {
    if (!authState.authUser) return;
    
    try {
      setResending(true);
      await sendEmailVerification(authState.authUser);
      setEmailResent(true);
      setTimeout(() => setEmailResent(false), 5000); // Reset after 5 seconds
    } catch (error) {
      console.error("Error resending verification email:", error);
    } finally {
      setResending(false);
    }
  };

  if (authState.loading) {
    return <LoadingSkeleton/>;
  }

  // No authenticated user - redirect to login
  if (!authState.authUser) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  // Email not verified - show verification dialog
  if (!authState.authUser.emailVerified) {
    return (
      <Dialog open={true} onClose={() => {}}>
        <DialogTitle>Verify Your Email</DialogTitle>
        <DialogContent>
          <DialogContentText>
            We've sent a verification link to <strong>{authState.authUser.email}</strong>.
            Please check your inbox and verify your email to continue.
          </DialogContentText>
          
          <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={handleResendVerification}
              disabled={resending || emailResent}
              startIcon={
                resending ? <CircularProgress size={20} /> : 
                emailResent ? <CheckCircleIcon /> : <RefreshIcon />
              }
              variant="outlined"
              color="primary"
              sx={{ mr: 2 }}
            >
              {resending ? 'Sending...' : 
               emailResent ? 'Email Sent!' : 'Resend Verification'}
            </Button>
            
            {emailResent && (
              <span style={{ color: '#4caf50', fontSize: '0.875rem' }}>
                Check your inbox again
              </span>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout}>Sign Out</Button>
          <Button 
            onClick={() => window.location.reload()}
            variant="contained"
            color="primary"
          >
            I've Verified
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Authenticated but no user data - redirect to login
  if (!authState.user) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  // Check user role
  const userRole = authState.user.isAdmin 
    ? "Admin" 
    : authState.user.isMentor 
      ? "Mentor" 
      : "Student";

  if (!allowedRoles.includes(userRole)) {
    const redirectPath = authState.user.isAdmin
      ? "/admin/home"
      : authState.user.isMentor
        ? "/mentor/home"
        : "/student/home";
        
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
};

export default React.memo(Protected);